import type {
  Case,
  CaseDocument,
  CaseEvent,
  CaseLink,
  CaseStatus,
  CaseType,
  Client,
  DocumentSource,
  DocumentType,
  EventType,
  Tag,
} from '@core/domain/model';
import type { Row, SqlValue } from './sqlite';

/**
 * Строка базы ↔ модель.
 *
 * Формат хранения повторяет Room до буквы: перечисления лежат строками своих
 * названий, логическое — числом 0/1, ссылки дела — одной строкой. Это не «так вышло»,
 * а условие того, чтобы архив с телефона открывался здесь.
 */

/**
 * Значение колонки: SQLite молчит про отсутствующие поля, а TypeScript про них знает —
 * `undefined` здесь такое же «нет значения», как и `null`.
 */
type Cell = SqlValue | undefined;

const bool = (value: Cell): boolean => value === 1 || value === '1';
const num = (value: Cell): number => Number(value ?? 0);
const str = (value: Cell): string => String(value ?? '');
const nullableStr = (value: Cell): string | null =>
  value === null || value === undefined ? null : String(value);
const nullableNum = (value: Cell): number | null =>
  value === null || value === undefined ? null : Number(value);

/**
 * Ссылки дела: `название\tадрес`, ссылки разделены переводом строки.
 *
 * Отдельная таблица была бы честнее, но ссылки всегда читаются и пишутся вместе
 * с делом и ни разу — сами по себе.
 */
export function parseLinks(value: Cell): CaseLink[] {
  if (!value) return [];

  return String(value)
    .split('\n')
    .filter(Boolean)
    .map((line) => {
      const [title = '', url = ''] = line.split('\t');
      return { title, url };
    })
    .filter((link) => link.url.length > 0);
}

export const formatLinks = (links: CaseLink[]): string | null =>
  links.length === 0 ? null : links.map((link) => `${link.title}\t${link.url}`).join('\n');

export const toCase = (row: Row): Case => ({
  id: str(row.id),
  title: str(row.title),
  clientId: nullableStr(row.clientId),
  type: str(row.type) as CaseType,
  status: str(row.status) as CaseStatus,
  typeOther: nullableStr(row.typeOther),
  description: nullableStr(row.description),
  notes: nullableStr(row.notes),
  links: parseLinks(row.links),
  isPinned: bool(row.isPinned),
  sortOrder: num(row.sortOrder),
  createdAt: num(row.createdAt),
  updatedAt: num(row.updatedAt),
});

export const toClient = (row: Row): Client => ({
  id: str(row.id),
  fullName: str(row.fullName),
  phone: nullableStr(row.phone),
  email: nullableStr(row.email),
  birthDate: nullableStr(row.birthDate),
  address: nullableStr(row.address),
  notes: nullableStr(row.notes),
  createdAt: num(row.createdAt),
  updatedAt: num(row.updatedAt),
});

export const toDocument = (row: Row): CaseDocument => ({
  id: str(row.id),
  title: str(row.title),
  caseId: nullableStr(row.caseId),
  clientId: nullableStr(row.clientId),
  fileName: str(row.fileName),
  mimeType: str(row.mimeType),
  sizeBytes: num(row.sizeBytes),
  pageCount: nullableNum(row.pageCount),
  isFavorite: bool(row.isFavorite),
  type: str(row.type) as DocumentType,
  source: str(row.source) as DocumentSource,
  sortOrder: num(row.sortOrder),
  addedAt: num(row.addedAt),
  createdAt: num(row.createdAt),
  updatedAt: num(row.updatedAt),
});

export const toEvent = (row: Row): CaseEvent => ({
  id: str(row.id),
  title: str(row.title),
  type: str(row.type) as EventType,
  startAt: num(row.startAt),
  endAt: nullableNum(row.endAt),
  description: nullableStr(row.description),
  caseId: nullableStr(row.caseId),
  clientId: nullableStr(row.clientId),
  reminderMinutesBefore: nullableNum(row.reminderMinutesBefore),
  isCompleted: bool(row.isCompleted),
  deadlineAt: nullableNum(row.deadlineAt),
  createdAt: num(row.createdAt),
  updatedAt: num(row.updatedAt),
});

export const toTag = (row: Row): Tag => ({ id: str(row.id), name: str(row.name) });

/** Идентификатор записи: UUID с первого дня — условие возможной синхронизации (D-04). */
export const newId = (): string => crypto.randomUUID();
